import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@material-ui/core';
import clsx from 'clsx';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '@clerk/clerk-react';

import { Plus } from '~/components/icons/Plus';
import { LeadIcon } from '~/components/icons/LeadIcon';
import { Settings } from '~/components/icons/Settings';
import { Trash } from '~/components/icons/Trash';
import { ArrowLeft } from '~/components/icons/ArrowLeft';
import { ArrowRight } from '~/components/icons/ArrowRight';
import { Close } from '~/components/icons/Close';
import { Reset } from '~/components/icons/Reset';
import { fetchCompanies } from '~/store/companiesList';
import DeleteUserDialog from '~/views/components/Dialogs/DeleteUserDialog';
import CreateOrUpdateUserDialog from '~/views/components/Dialogs/CreateOrUpdateUserDialog';
import RestrictionDeleteDialog from '~/views/components/Dialogs/RestrictionDeleteDialog';
import RestrictionUpdateDialog from '~/views/components/Dialogs/RestrictionUpdateDialog';
import * as users from '~/store/allUsersList';

const useStyles = makeStyles(() => ({
  root: {
    width: '100vw',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    background: 'var(--surface-pale, #F3F4F6)',
  },
  container: {
    display: 'flex',
    width: '1200px',
    flexDirection: 'column',
    alignItems: 'center',
    flexShrink: 0,
  },
  header: {
    display: 'flex',
    padding:
      'var(--utilities-size-1-000, 16px) 0px var(--utilities-size-3-000, 48px) var(--utilities-size-1-000, 16px)',
    justifyContent: 'space-between',
    alignItems: 'center',
    alignSelf: 'stretch',
  },
  tableContainer: {
    display: 'flex',
    paddingBottom: 'var(--utilities-size-3-000, 48px)',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 'var(--utilities-size-1-000, 16px)',
    alignSelf: 'stretch',
  },
  titleSection: {
    display: 'flex',
    width: '660px',
    alignItems: 'center',
    gap: 'var(--utilities-size-2-000, 32px)',
  },
  roleSection: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--utilities-size-1-000, 16px)',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--utilities-size-1-000, 16px)',
  },
  title: {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: '1',
    overflow: 'hidden',
    color: 'var(--content-primary, #2C313A)',
    textOverflow: 'ellipsis',
    fontFamily: 'var(--typography-font-family-default, Inter)',
    fontSize: 'var(--typography-font-size-lg, 20px)',
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: 'var(--typography-line-height-lg-line, 28px)',
    letterSpacing: 'var(--typography-letter-spacing-default, 0px)',
  },
  roleContainer: {
    display: 'flex',
    padding: 'var(--utilities-size-none, 0px)',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 'var(--utilities-size-none, 0px)',
  },
  role: {
    alignSelf: 'stretch',
    color: 'var(--content-secondary, #6B7280)',
    textAlign: 'right',
    fontFamily: 'var(--typography-font-family-default, Inter)',
    fontSize: 'var(--typography-font-size-xs, 12px)',
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: 'var(--typography-line-height-xs-line, 16px)',
    letterSpacing: 'var(--typography-letter-spacing-default, 0px)',
  },
  email: {
    alignSelf: 'stretch',
    color: 'var(--content-secondary, #6B7280)',
    textAlign: 'right',
    fontFamily: 'var(--typography-font-family-default, Inter)',
    fontSize: 'var(--typography-font-size-xs, 12px)',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: 'var(--typography-line-height-xs-line, 16px)',
    letterSpacing: 'var(--typography-letter-spacing-default, 0px)',
  },
  logoutButton: {
    display: 'flex',
    minHeight: 'var(--utilities-size-2-250, 36px)',
    minWidth: '44px',
    padding:
      'var(--utilities-size-0-500, 8px) var(--utilities-size-0-750, 12px)',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-500, 8px)',
    borderRadius: 'var(--utilities-border-radius-default, 8px)',
    background: 'var(--surface-tint-weak, rgba(23, 28, 38, 0.05))',
    boxShadow: '0px 1px 2px 0px rgba(23, 28, 38, 0.05)',
    '&:hover': {
      backgroundColor:
        'var(--surface-hover-tint-weak-hover, rgba(23, 28, 38, 0.10))',
    },
  },
  createUser: {
    display: 'flex',
    justifyContent: 'center',
    gap: 'var(--utilities-size-0-500, 8px)',
    height: 'var(--utilities-size-2-250, 36px)',
    minHeight: 'var(--utilities-size-2-250, 36px)',
    padding:
      'var(--utilities-size-0-500, 8px) var(--utilities-size-0-750, 12px)',
    alignItems: 'center',
    borderRadius: 'var(--utilities-border-radius-default, 8px)',
    border: '1px solid var(--border-control-default, rgba(23, 28, 38, 0.20))',
    '&:hover': {
      cursor: 'pointer',
      borderRadius: 'var(--utilities-border-radius-default, 8px)',
      border:
        '1px solid var(--border-hover-accent-weak--hover, rgba(70, 86, 229, 0.40))',
      background: 'var(--surface-hover-default-hover, #F8F9FC)',
      '& $createUserButtonText': {
        color: 'var(--content-hover-primary--hover, #5646E5)',
      },
    },
  },
  createUserButtonText: {
    color: 'var(--content-primary, #2C313A)',
    textAlign: 'center',
    fontFamily: 'var(--typography-font-family-default, Inter)',
    fontSize: 'var(--typography-font-size-sm, 14px)',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: 'var(--typography-line-height-sm-line, 20px)',
    letterSpacing: 'var(--typography-letter-spacing-default, 0px)',
    textTransform: 'none',
  },
  searchSection: {
    display: 'flex',
    height: '44px',
    alignItems: 'center',
    alignSelf: 'stretch',
    borderRadius: 'var(--utilities-border-radius-lg, 12px)',
    border: '1px solid var(--border-control-default, rgba(23, 28, 38, 0.20))',
    background: 'var(--surface-default, #FFF)',
    boxShadow: '0px 1px 2px 0px rgba(23, 28, 38, 0.05)',
  },
  searchInputContainer: {
    display: 'flex',
    width: '588px',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-500, 8px)',
    flex: '1 0 0',
  },
  searchInput: {
    width: '100%',
    height: '22px',
    padding: 'var(--utilities-size-0-750) 30px',
    alignItems: 'center',
    alignSelf: 'stretch',
    boxSizing: 'border-box',
    backgroundImage: 'url(/public/static/icons/seenic/search.svg)',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'var(--utilities-size-0-500) 5px',
    backgroundSize: 'var(--utilities-size-1-000)',
    color: 'var(--content-primary)',
    '&:hover': {
      backgroundImage: 'url(/public/static/icons/seenic/search.svg)',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'var(--utilities-size-0-500) 5px',
      backgroundSize: 'var(--utilities-size-1-000)',
    },
    '&:focus, &:focus-visible': {
      backgroundImage: 'url(/public/static/icons/seenic/search.svg)',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'var(--utilities-size-0-500) 5px',
      backgroundSize: 'var(--utilities-size-1-000)',
      outline: 'unset',
    },
    '&:-internal-autofill-selected': {
      backgroundImage: 'url(/public/static/icons/seenic/search.svg)',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'var(--utilities-size-0-500) 5px',
      backgroundSize: 'var(--utilities-size-1-000)',
    },
  },
  searchClean: {
    width: 'var(--utilities-size-1-000)',
    height: 'var(--utilities-size-1-000)',
    marginRight: 'var(--utilities-size-0-500)',
    cursor: 'pointer',
  },
  resetIcon: {
    width: '20px',
    height: '20px',
    aspectRatio: '1/1',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  chooseCompanyContainer: {
    display: 'flex',
    width: '280px',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-500, 8px)',
    color: 'var(--content-primary, #2C313A)',
    fontFamily: 'var(--typography-font-family-default, Inter)',
    fontSize: 'var(--typography-font-size-sm, 14px)',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: 'var(--typography-line-height-sm-line, 20px)',
    letterSpacing: 'var(--typography-letter-spacing-default, 0px)',
  },
  chooseCompanySelect: {
    appearance: 'none',
    MozAppearance: 'none',
    WebkitAppearance: 'none',
    width: '100%',
    paddingLeft: 'var(--utilities-size-1-000, 16px)',
    backgroundImage: 'url(/public/static/icons/seenic/chevron-down.svg)',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '20px 20px',
    backgroundPosition: 'right 12px center',
    padding: '8px',
    '&:focus, &:focus-visible': {
      outline: 'unset',
      border: 'none',
    },
  },
  choosePropertyContainer: {
    display: 'flex',
    width: '280px',
    alignItems: 'center',
  },
  choosePropertySelect: {
    appearance: 'none',
    MozAppearance: 'none',
    WebkitAppearance: 'none',
    width: '100%',
    paddingLeft: 'var(--utilities-size-1-000, 16px)',
    backgroundImage: 'url(/public/static/icons/seenic/chevron-down.svg)',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '20px 20px',
    backgroundPosition: 'right 12px center',
    color: 'var(--content-primary, #2C313A)',
    fontFamily: 'var(--typography-font-family-default, Inter)',
    fontSize: 'var(--typography-font-size-sm, 14px)',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: 'var(--typography-line-height-sm-line, 20px)',
    letterSpacing: 'var(--typography-letter-spacing-default, 0px)',
    '&:disabled': {
      backgroundImage:
        'url(/public/static/icons/seenic/chevron-down-disabled.svg)',
      backgroundRepeat: 'no-repeat',
      backgroundSize: '20px 20px',
      backgroundPosition: 'right 12px center',
      color: 'var(--content-primary-muted, rgba(23, 28, 38, 0.20))',
      cursor: 'not-allowed',
    },
    '&:focus, &:focus-visible': {
      outline: 'unset',
      border: 'none',
    },
  },
  searchResetContainer: {
    display: 'flex',
    width: '52px',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-500, 8px)',
    justifyContent: 'center',
  },
  searchSectionSeparator: {
    width: '1px',
    height: '42px',
    background: 'var(--border-default, rgba(23, 28, 38, 0.10))',
  },
  tableSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    borderRadius: 'var(--utilities-border-radius-lg, 12px)',
    background: 'var(--surface-default, #FFF)',
    boxShadow: '0px 1px 2px 0px rgba(23, 28, 38, 0.05)',
  },
  paginationSection: {
    height: '56px',
    alignItems: 'flex-start',
    alignSelf: 'stretch',
  },
  tableHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'stretch',
  },
  tableHeaderIdCell: {
    display: 'flex',
    width: '100px',
    padding:
      'var(--utilities-size-1-000, 16px) var(--utilities-size-1-500, 24px)',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-250, 4px)',
    borderBottom: '1px solid var(--border-rude-weak, rgba(23, 28, 38, 0.40))',
    borderTopLeftRadius: 'var(--utilities-border-radius-default, 0.5rem)',
    '&:hover': {
      '& $tableHeaderCell': {
        color: 'var(--content-primary, #2C313A)',
      },
    },
  },
  tableHeaderUserCell: {
    display: 'flex',
    width: '320px',
    padding:
      'var(--utilities-size-1-000, 16px) var(--utilities-size-1-500, 24px) var(--utilities-size-1-000, 16px) 0px',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-250, 4px)',
    borderBottom: '1px solid var(--border-rude-weak, rgba(23, 28, 38, 0.40))',
    '&:hover': {
      '& $tableHeaderCell': {
        color: 'var(--content-primary, #2C313A)',
      },
    },
  },
  tableHeaderCompanyCell: {
    display: 'flex',
    width: '200px',
    padding:
      'var(--utilities-size-1-000, 16px) var(--utilities-size-1-500, 24px) var(--utilities-size-1-000, 16px) 0px',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-250, 4px)',
    borderBottom: '1px solid var(--border-rude-weak, rgba(23, 28, 38, 0.40))',
    '&:hover': {
      '& $tableHeaderCell': {
        color: 'var(--content-primary, #2C313A)',
      },
    },
  },
  tableHeaderPropertiesCell: {
    display: 'flex',
    width: '356px',
    padding:
      'var(--utilities-size-1-000, 16px) var(--utilities-size-1-500, 24px) var(--utilities-size-1-000, 16px) 0px',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-250, 4px)',
    flex: '1 0 0',
    borderBottom: '1px solid var(--border-rude-weak, rgba(23, 28, 38, 0.40))',
    '&:hover': {
      '& $tableHeaderCell': {
        color: 'var(--content-primary, #2C313A)',
      },
    },
  },
  tableHeaderRoleCell: {
    display: 'flex',
    width: '80px',
    padding:
      'var(--utilities-size-1-000, 16px) var(--utilities-size-1-500, 24px) var(--utilities-size-1-000, 16px) 0px',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-250, 4px)',
    borderBottom: '1px solid var(--border-rude-weak, rgba(23, 28, 38, 0.40))',
    '&:hover': {
      '& $tableHeaderCell': {
        color: 'var(--content-primary, #2C313A)',
      },
    },
  },
  tableHeaderManageCell: {
    display: 'flex',
    width: '144px',
    padding:
      'var(--utilities-size-1-000, 16px) var(--utilities-size-1-500, 24px) var(--utilities-size-1-000, 16px) 0px',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-250, 4px)',
    borderBottom: '1px solid var(--border-rude-weak, rgba(23, 28, 38, 0.40))',
    borderTopRightRadius: 'var(--utilities-border-radius-default, 0.5rem)',
    '&:hover': {
      '& $tableHeaderCell': {
        color: 'var(--content-primary, #2C313A)',
      },
    },
  },
  tableHeaderCell: {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: '1',
    overflow: 'hidden',
    color: 'var(--content-primary-weak, rgba(23, 28, 38, 0.40))',
    textOverflow: 'ellipsis',
    fontFamily: 'var(--typography-font-family-default, Inter)',
    fontSize: 'var(--typography-font-size-sm, 14px)',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: 'var(--typography-line-height-sm-line, 20px)',
    letterSpacing: 'var(--typography-letter-spacing-default, 0px)',
  },
  sortIcon: {
    cursor: 'pointer',
    marginLeft: 'var(--utilities-size-0-375)',
    display: 'inline',
  },
  sortIconAsc: {
    transform: 'rotate(180deg)',
  },
  tableBodyRow: {
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'stretch',
    '&:last-child': {
      '& .MuiTableCell-root': {
        borderBottom: 'none',
      },
    },
  },
  tableBodyIdCell: {
    display: 'flex',
    width: '100px',
    padding:
      'var(--utilities-size-1-000, 16px) var(--utilities-size-1-500, 24px)',
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    '&:not(:last-child)': {
      borderBottom: '1px solid var(--border-default, rgba(23, 28, 38, 0.10))',
    },
  },
  tableBodyUserCell: {
    display: 'flex',
    width: '320px',
    padding:
      'var(--utilities-size-1-000, 16px) var(--utilities-size-1-500, 24px) var(--utilities-size-1-000, 16px) 0px',
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    borderBottom: '1px solid var(--border-default, rgba(23, 28, 38, 0.10))',
  },
  tableBodyCompanyCell: {
    display: 'flex',
    width: '200px',
    padding:
      'var(--utilities-size-1-000, 16px) var(--utilities-size-1-500, 24px) var(--utilities-size-1-000, 16px) 0px',
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    borderBottom: '1px solid var(--border-default, rgba(23, 28, 38, 0.10))',
  },
  tableBodyPropertiesCell: {
    display: 'flex',
    width: '356px',
    padding:
      'var(--utilities-size-1-000, 16px) var(--utilities-size-1-500, 24px) var(--utilities-size-1-000, 16px) 0px',
    alignItems: 'flex-start',
    flex: '1 0 0',
    alignSelf: 'stretch',
    borderBottom: '1px solid var(--border-default, rgba(23, 28, 38, 0.10))',
  },
  tableBodyRoleCell: {
    display: 'flex',
    width: '80px',
    padding:
      'var(--utilities-size-1-000, 16px) var(--utilities-size-1-500, 24px) var(--utilities-size-1-000, 16px) 0px',
    alignItems: 'flex-start',
    gap: 'var(--utilities-size-0-250, 4px)',
    alignSelf: 'stretch',
    borderBottom: '1px solid var(--border-default, rgba(23, 28, 38, 0.10))',
  },
  tableBodyManageCell: {
    display: 'flex',
    width: '144px',
    padding:
      'var(--utilities-size-1-000, 16px) var(--utilities-size-1-500, 24px) var(--utilities-size-1-000, 16px) 0px',
    alignItems: 'flex-start',
    gap: 'var(--utilities-size-1-500, 24px)',
    alignSelf: 'stretch',
    borderBottom: '1px solid var(--border-default, rgba(23, 28, 38, 0.10))',
  },
  tableBodyCell: {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 7,
    flex: '1 0 0',
    overflow: 'hidden',
    color: 'var(--content-primary, #2C313A)',
    textOverflow: 'ellipsis',
    fontFamily: 'var(--typography-font-family-default, Inter)',
    fontSize: 'var(--typography-font-size-sm, 14px)',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: 'var(--typography-line-height-sm-line, 20px)',
    letterSpacing: 'var(--typography-letter-spacing-default, 0px)',
  },
  tableBodyEmailCell: {
    overflow: 'hidden',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    color: 'var(--content-primary-weak, rgba(23, 28, 38, 0.40))',
    textOverflow: 'ellipsis',
    fontFamily: 'var(--typography-font-family-default, Inter)',
    fontSize: 'var(--typography-font-size-sm, 14px)',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: 'var(--typography-line-height-sm-line, 20px)',
    letterSpacing: 'var(--typography-letter-spacing-default, 0px)',
  },
  roleContainerSuperadmin: {
    display: 'flex',
    width: '56px',
    padding:
      'var(--utilities-size-0-250, 4px) var(--utilities-size-0-500, 8px)',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 'var(--utilities-border-radius-md, 4px)',
    background: 'var(--surface-label-red, #FEE2E2)',
  },
  roleContainerAdmin: {
    display: 'flex',
    width: '56px',
    padding:
      'var(--utilities-size-0-250, 4px) var(--utilities-size-0-500, 8px)',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 'var(--utilities-border-radius-md, 4px)',
    background: 'var(--surface-label-yellow, #FEF3C7)',
  },
  roleContainerClient: {
    display: 'flex',
    width: '56px',
    padding:
      'var(--utilities-size-0-250, 4px) var(--utilities-size-0-500, 8px)',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 'var(--utilities-border-radius-md, 4px)',
    background: 'var(--surface-label-indigo, #EBEDFF)',
  },
  roleTextSuperadmin: {
    alignSelf: 'stretch',
    color: 'var(--content-label-red, #DC2626)',
    textAlign: 'center',
    fontFamily: 'var(--typography-font-family-default, Inter)',
    fontSize: 'var(--typography-font-size-xs, 12px)',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: 'var(--typography-line-height-xs-line, 16px)',
    letterSpacing: 'var(--typography-letter-spacing-default, 0px)',
  },
  roleTextAdmin: {
    alignSelf: 'stretch',
    color: 'var(--content-label-yellow, #D97706)',
    textAlign: 'center',
    fontFamily: 'var(--typography-font-family-default, Inter)',
    fontSize: 'var(--typography-font-size-xs, 12px)',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: 'var(--typography-line-height-xs-line, 16px)',
    letterSpacing: 'var(--typography-letter-spacing-default, 0px)',
  },
  roleTextClient: {
    alignSelf: 'stretch',
    color: 'var(--content-label-indigo, #5646E5)',
    textAlign: 'center',
    fontFamily: 'var(--typography-font-family-default, Inter)',
    fontSize: 'var(--typography-font-size-xs, 12px)',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: 'var(--typography-line-height-xs-line, 16px)',
    letterSpacing: 'var(--typography-letter-spacing-default, 0px)',
  },
  tableBodyManageSeparator: {
    width: '1px',
    height: '24px',
    background: 'var(--border-default, rgba(23, 28, 38, 0.10))',
  },
  rowHover: {
    '&:hover': {
      backgroundColor: 'var(--surface-hover-default--hover, #F8F9FC)',
      '&:last-child': {
        borderBottomLeftRadius:
          'var(--utilities-border-radius-default, 0.5rem)',
        borderBottomRightRadius:
          'var(--utilities-border-radius-default, 0.5rem)',
      },
    },
  },
  manageIcon: {
    width: '24px',
    height: '24px',
    aspectRatio: '1/1',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  footer: {
    display: 'flex',
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    height: 'var(--utilities-size-3-500)',
    backgroundColor: 'var(--surface-default)',
    borderRadius: 'var(--utilities-border-radius-lg, 12px)',
    boxShadow: '0px 1px 2px 0px rgba(23, 28, 38, 0.05)',
  },
  footerPart1: {
    display: 'flex',
    padding: 'var(--utilities-size-none) var(--utilities-size-1-500)',
    justifyContent: 'flex-end',
    alignItems: 'center',
    flex: '1 0 0',
    alignSelf: 'stretch',
    borderRadius:
      'var(--utilities-border-radius-lg, 12px) 0px 0px var(--utilities-border-radius-lg, 12px)',
    background: 'var(--surface-default)',
    cursor: 'pointer',
    '&:hover': {
      background: 'var(--surface-hover-default-hover)',
    },
    '& .MuiTouchRipple-root': {
      position: 'unset',
    },
  },
  footerPart2: {
    display: 'flex',
    width: '160px',
    padding: 'var(--utilities-size-none) var(--utilities-size-1-000)',
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'stretch',
    borderLeft: '1px solid var(--border-default)',
    background: 'var(--surface-default)',
  },
  pagination: {
    color: 'var(--content-secondary)',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-sm)',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: 'var(--typography-line-height-sm-line)',
    letterSpacing: 'var(--typography-letter-spacing-default)',
  },
  footerPart3: {
    display: 'flex',
    padding: 'var(--utilities-size-none) var(--utilities-size-1-500)',
    alignItems: 'center',
    flex: '1 0 0',
    alignSelf: 'stretch',
    justifyContent: 'flex-Start',
    borderRadius:
      '0px var(--utilities-border-radius-lg, 12px) var(--utilities-border-radius-lg, 12px) 0px',
    borderLeft: '1px solid var(--border-default)',
    background: 'var(--surface-default)',
    cursor: 'pointer',
    '&:hover': {
      background: 'var(--surface-hover-default-hover)',
    },
    '& .MuiTouchRipple-root': {
      position: 'unset',
    },
  },
  footerNoRec: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-around',
    alignSelf: 'stretch',
    height: 'var(--utilities-size-3-500)',
    padding: 'var(--utilities-size-5-000, 80px) 0px',
  },
}));

const UsersDashboard = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const user = useSelector(state => state.auth.user);
  const [loading, setLoading] = useState(false);
  const [hoveredCreateUserButton, setHoveredCreateUserButton] = useState(null);
  const [hoveredBodyRow, setHoveredBodyRow] = useState(null);
  const [hoveredTrashIcon, setHoveredTrashIcon] = useState(null);
  const [hoveredArrowLeft, setHoveredArrowLeft] = useState(null);
  const [hoveredArrowRight, setHoveredArrowRight] = useState(null);
  const [hoveredReset, setHoveredReset] = useState(null);
  const [page, setPage] = useState(0);
  const [arrowIconColor, setArrowIconColor] = useState(null);
  const [sortByCompany, setSortByCompany] = useState(false);
  const [isSortedAsc, setIsSortedAsc] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [propertyFilter, setPropertyFilter] = useState('');
  const [propertiesList, setPropertiesList] = useState([]);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isCreateDialogOpen, setCreateDialogOpen] = useState(false);
  const [isRestrictionDeleteDialogOpen, setRestrictionDeleteDialogOpen] =
    useState(false);
  const [isRestrictionUpdateDialogOpen, setRestrictionUpdateDialogOpen] =
    useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const { signOut, getToken } = useAuth();

  const isSuperAdmin = user.role === 'superadmin';
  const authUserId = user.id;

  const fullCompaniesList = useSelector(state => state.companiesList).filter(
    company => !company.isEmpty,
  );

  const fullUsersList = useSelector(state => state.usersList);

  const companiesList = isSuperAdmin
    ? fullCompaniesList
    : fullCompaniesList.filter(company =>
        user.companiesAccessList.some(access => access.name === company.name),
      );

  const usersList = isSuperAdmin
    ? fullUsersList
    : fullUsersList.filter(user => user.role !== 'superadmin');

  const filteredUsersByCompany = usersList.filter(user =>
    selectedCompany
      ? user.companiesAccessList &&
        user.companiesAccessList.some(
          company => company.name === selectedCompany,
        )
      : true,
  );

  const filteredUsersByCompanyAndProp = filteredUsersByCompany.filter(user =>
    propertyFilter
      ? user.propertiesAccessList &&
        user.propertiesAccessList.some(
          company => company.name === propertyFilter,
        )
      : true,
  );

  const filteredUsersBySearch = filteredUsersByCompanyAndProp.filter(user =>
    searchInput
      ? (user.firstName &&
          user.firstName.toLowerCase().includes(searchInput.toLowerCase())) ||
        (user.lastName &&
          user.lastName.toLowerCase().includes(searchInput.toLowerCase())) ||
        (user.email &&
          user.email.toLowerCase().includes(searchInput.toLowerCase())) ||
        (user.companiesAccessList &&
          user.companiesAccessList.some(company =>
            company.name.toLowerCase().includes(searchInput.toLowerCase()),
          )) ||
        (user.propertiesAccessList &&
          user.propertiesAccessList.some(company =>
            company.name.toLowerCase().includes(searchInput.toLowerCase()),
          ))
      : true,
  );

  const rowsPerPage = 15;
  const priority = {
    superadmin: 1,
    admin: 2,
    client: 3,
  };

  if (!user) return null;

  const role = user.role.slice(0, 1).toUpperCase() + user.role.slice(1);

  const handleLogout = async () => {
    setLoading(true);
    try {
      await signOut();
    } catch (err) {
      console.error(err);
    } finally {
      window.location.href = `${window.location.origin}/login`;
      setLoading(false);
    }
  };

  const handleOpenDeleteDialog = id => {
    if (id !== authUserId) {
      setDeleteDialogOpen(true);
    }
  };

  const handleOpenRestrictionDeleteDialog = id => {
    if (id !== authUserId) {
      setRestrictionDeleteDialogOpen(true);
    }
  };

  const handleOpenRestrictionUpdateDialog = id => {
    if (id === authUserId) {
      setRestrictionUpdateDialogOpen(true);
    }
  };

  const handleCloseRestrictionDeleteDialog = () => {
    setRestrictionDeleteDialogOpen(false);
  };

  const handleCloseRestrictionUpdateDialog = () => {
    setRestrictionUpdateDialogOpen(false);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleOpenCreateDialog = () => {
    setCreateDialogOpen(true);
    setCurrentUser(null);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setCurrentUser(null);
  };

  async function createUser(user) {
    const token = await getToken();
    return dispatch(users.createUser(user, token))
      .then(() => toast.success('User has been created successfully'))
      .catch(({ message }) => toast.error(message));
  }

  const handleCreateConfirmation = async user => {
    handleCloseDeleteDialog();
    await createUser(user);
    const token = await getToken();
    dispatch(users.fetchAllUsers(token)).then(res => res);
  };

  async function updateUser(user) {
    const token = await getToken();
    return dispatch(users.updateUser(user, token))
      .then(() => toast.success('User has been updated successfully'))
      .catch(({ message }) => toast.error(message));
  }

  const handleUpdateConfirmation = async user => {
    handleCloseDeleteDialog();
    await updateUser(user);
    const token = await getToken();
    dispatch(users.fetchAllUsers(token)).then(res => res);
  };

  const openUpdateDialog = user => {
    setCurrentUser(user);
    if (user.id === authUserId) {
      setRestrictionUpdateDialogOpen(true);
    } else {
      setCreateDialogOpen(true);
    }
  };

  async function deleteUser(userId) {
    const token = await getToken();
    return dispatch(users.deleteUser(userId, token))
      .then(() => toast.success('User has been deleted successfully'))
      .catch(({ message }) => toast.error(message));
  }

  const handleDeleteConfirmation = async userId => {
    handleCloseDeleteDialog();
    await deleteUser(userId);
    const token = await getToken();
    dispatch(users.fetchAllUsers(token)).then(res => res);
  };

  const handleSortOrderChange = () => {
    setSortByCompany(!sortByCompany);
    setIsSortedAsc(prevState => !prevState);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleClearSearch = () => {
    setSearchInput('');
  };

  const handleReset = () => {
    setSearchInput('');
    setSelectedCompany('');
    setPropertyFilter('');
  };

  const sortedUsersList = [...filteredUsersBySearch].sort((a, b) => {
    if (sortByCompany) {
      const companyA = a.companyName.toLowerCase() || '';
      const companyB = b.companyName.toLowerCase() || '';
      if (companyA < companyB) return -1;
      if (companyA > companyB) return 1;
      return 0;
    }
    return priority[a.role] - priority[b.role];
  });

  const paginatedData = () =>
    sortedUsersList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      const token = await getToken();
      if (!mounted) return;
      dispatch(fetchCompanies()).then(res => res);
      dispatch(users.fetchAllUsers(token)).then(res => res);
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <Box className={classes.root}>
      <ToastContainer />
      <Box className={classes.container}>
        <Box className={classes.header}>
          <Box className={classes.titleSection}>
            <Box className={classes.titleContainer}>
              <img
                style={{ width: '28px', height: '28px' }}
                src={'/static/icons/seenic/logo.svg'}
                alt="Company logo"
              />
              <Box className={classes.title}>Users dashboard</Box>
            </Box>
            <Box
              className={classes.createUser}
              disabled={loading}
              onMouseEnter={() => setHoveredCreateUserButton(true)}
              onMouseLeave={() => setHoveredCreateUserButton(null)}
              onClick={handleOpenCreateDialog}
            >
              <Plus
                style={{ width: '20px', height: '20px' }}
                color={hoveredCreateUserButton ? '#5646E5' : '#2C313A'}
              />
              <Box className={classes.createUserButtonText}>New user</Box>
            </Box>
          </Box>
          <CreateOrUpdateUserDialog
            isSuperAdmin={isSuperAdmin}
            open={isCreateDialogOpen}
            onClose={handleCloseCreateDialog}
            setDialogOpen={setCreateDialogOpen}
            authUser={user}
            initialData={currentUser}
            fullCompaniesList={fullCompaniesList}
            companiesList={companiesList}
            handleCreateConfirmation={handleCreateConfirmation}
            handleUpdateConfirmation={handleUpdateConfirmation}
          />
          <RestrictionDeleteDialog
            open={!isSuperAdmin && isRestrictionDeleteDialogOpen}
            onClose={handleCloseRestrictionDeleteDialog}
            setDialogOpen={setRestrictionDeleteDialogOpen}
          />
          <RestrictionUpdateDialog
            open={isRestrictionUpdateDialogOpen}
            onClose={handleCloseRestrictionUpdateDialog}
            setDialogOpen={setRestrictionUpdateDialogOpen}
          />
          <Box className={classes.roleSection}>
            <Box className={classes.roleContainer}>
              <Box className={classes.role}>{role}</Box>
              <Box className={classes.email}>{user.email}</Box>
            </Box>
            <Button
              className={classes.logoutButton}
              onClick={handleLogout}
              disabled={loading}
            >
              <img
                style={{ width: '20px', height: '20px' }}
                src={'/static/icons/seenic/log-out.svg'}
                alt="Logout"
              />
            </Button>
          </Box>
        </Box>
        <Box className={classes.tableContainer}>
          <Box className={classes.searchSection}>
            <Box className={classes.searchInputContainer}>
              <input
                name="text"
                className={classes.searchInput}
                value={searchInput}
                placeholder="Search"
                onChange={e => {
                  setSearchInput(e.target.value);
                  setPage(0);
                }}
              />
              {searchInput.length > 0 && (
                <Close
                  className={classes.searchClean}
                  onClick={handleClearSearch}
                />
              )}
            </Box>
            <Box className={classes.searchSectionSeparator} />
            <Box className={classes.chooseCompanyContainer}>
              <select
                className={classes.chooseCompanySelect}
                value={selectedCompany}
                onChange={e => {
                  setPage(0);
                  setSelectedCompany(e.target.value);
                  setPropertiesList(
                    companiesList.filter(
                      company => company.name === e.target.value,
                    ),
                  );
                }}
              >
                <option value="">Choose company...</option>
                {companiesList.map(company => (
                  <option key={company.id} value={company.name}>
                    {company.name}
                  </option>
                ))}
              </select>
            </Box>
            <Box className={classes.searchSectionSeparator} />
            <Box className={classes.choosePropertyContainer}>
              <select
                className={classes.choosePropertySelect}
                onChange={e => setPropertyFilter(e.target.value)}
                disabled={!selectedCompany}
                value={propertyFilter}
              >
                <option value="">Choose property...</option>
                {propertiesList.length > 0 &&
                  propertiesList[0].properties.map(property => (
                    <option key={property.id} value={property.name}>
                      {property.name}
                    </option>
                  ))}
              </select>
            </Box>
            <Box className={classes.searchSectionSeparator} />
            <Box className={classes.searchResetContainer}>
              <Box
                onMouseEnter={() => setHoveredReset(true)}
                onMouseLeave={() => setHoveredReset(null)}
              >
                <Reset
                  className={classes.resetIcon}
                  onClick={handleReset}
                  fillOpacity={hoveredReset ? 1 : 0.2}
                />
              </Box>
            </Box>
          </Box>
          {sortedUsersList.length > 0 ? (
            <>
              <Box className={classes.tableSection}>
                <TableContainer>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow className={classes.tableHeaderRow}>
                        <TableCell className={classes.tableHeaderIdCell}>
                          <Box className={classes.tableHeaderCell}>ID</Box>
                        </TableCell>
                        <TableCell className={classes.tableHeaderUserCell}>
                          <Box className={classes.tableHeaderCell}>User</Box>
                        </TableCell>
                        <TableCell
                          className={classes.tableHeaderCompanyCell}
                          onMouseEnter={() => setArrowIconColor('2C313A')}
                          onMouseLeave={() => setArrowIconColor(null)}
                        >
                          <Box className={classes.tableHeaderCell}>
                            Company
                            <LeadIcon
                              onClick={handleSortOrderChange}
                              className={clsx(
                                classes.sortIcon,
                                !isSortedAsc && classes.sortIconAsc,
                              )}
                              color={arrowIconColor || '171C26'}
                            />
                          </Box>
                        </TableCell>
                        <TableCell
                          className={classes.tableHeaderPropertiesCell}
                        >
                          <Box className={classes.tableHeaderCell}>
                            Properties
                          </Box>
                        </TableCell>
                        <TableCell className={classes.tableHeaderRoleCell}>
                          <Box className={classes.tableHeaderCell}>Role</Box>
                        </TableCell>
                        <TableCell className={classes.tableHeaderManageCell}>
                          <Box className={classes.tableHeaderCell}>Manage</Box>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedData(sortedUsersList).map((user, index) => (
                        <React.Fragment key={user.id}>
                          <TableRow
                            key={user.id}
                            onClick={() => openUpdateDialog(user)}
                            className={`${classes.tableBodyRow} ${classes.rowHover}`}
                            onMouseEnter={() => setHoveredBodyRow(user.id)}
                            onMouseLeave={() => setHoveredBodyRow(null)}
                          >
                            <TableCell className={classes.tableBodyIdCell}>
                              <Box className={classes.tableBodyCell}>
                                {user.id}
                              </Box>
                            </TableCell>
                            <TableCell className={classes.tableBodyUserCell}>
                              <Box>
                                <Box className={classes.tableBodyCell}>
                                  {user.firstName && user.lastName
                                    ? `${user.firstName} ${user.lastName}`
                                    : 'FirstName LastName'}
                                </Box>
                                <Box className={classes.tableBodyEmailCell}>
                                  {user.email}
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell className={classes.tableBodyCompanyCell}>
                              <Box className={classes.tableBodyCell}>
                                {user.accessAllCompanies
                                  ? 'All'
                                  : user.companiesAccessList
                                  ? user.companiesAccessList
                                      .map(company => company.name)
                                      .join(', ')
                                  : user.companyName}
                              </Box>
                            </TableCell>
                            <TableCell
                              className={classes.tableBodyPropertiesCell}
                            >
                              <Box className={classes.tableBodyCell}>
                                {user.accessAllProperties
                                  ? 'All'
                                  : user.propertiesAccessList
                                  ? user.propertiesAccessList
                                      .map(property => property.name)
                                      .join(', ')
                                  : '-'}
                              </Box>
                            </TableCell>
                            <TableCell className={classes.tableBodyRoleCell}>
                              <Box
                                className={clsx({
                                  [classes.roleContainerSuperadmin]:
                                    user.role === 'superadmin',
                                  [classes.roleContainerAdmin]:
                                    user.role === 'admin',
                                  [classes.roleContainerClient]:
                                    user.role === 'client',
                                })}
                              >
                                <Box
                                  className={clsx({
                                    [classes.roleTextSuperadmin]:
                                      user.role === 'superadmin',
                                    [classes.roleTextAdmin]:
                                      user.role === 'admin',
                                    [classes.roleTextClient]:
                                      user.role === 'client',
                                  })}
                                >
                                  {user.role === 'superadmin'
                                    ? 'Super'
                                    : user.role === 'admin'
                                    ? 'Admin'
                                    : 'Client'}
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell className={classes.tableBodyManageCell}>
                              <Box
                                className={classes.tableBodyManageSeparator}
                              />
                              <Settings
                                className={classes.manageIcon}
                                color={
                                  hoveredBodyRow === user.id
                                    ? '5E59F3'
                                    : '2C313A'
                                }
                              />
                              <Box
                                className={classes.tableBodyManageSeparator}
                              />
                              <Box
                                onMouseEnter={() =>
                                  setHoveredTrashIcon(user.id)
                                }
                                onMouseLeave={() => setHoveredTrashIcon(null)}
                              >
                                <Trash
                                  className={classes.manageIcon}
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleOpenDeleteDialog(user.id);
                                    handleOpenRestrictionDeleteDialog(user.id);
                                    handleOpenRestrictionUpdateDialog(user.id);
                                    setCurrentUser(user);
                                  }}
                                  fillOpacity={
                                    hoveredTrashIcon === user.id ? 1 : 0.4
                                  }
                                />
                              </Box>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      ))}
                      <DeleteUserDialog
                        open={isSuperAdmin && isDeleteDialogOpen}
                        onClose={handleCloseDeleteDialog}
                        onConfirm={() =>
                          handleDeleteConfirmation(currentUser.id)
                        }
                        setDialogOpen={setDeleteDialogOpen}
                        userRole={currentUser && currentUser.role}
                        userEmail={currentUser && currentUser.email}
                        userFirstName={currentUser && currentUser.firstName}
                        userLastName={currentUser && currentUser.lastName}
                      />
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              {sortedUsersList.length > rowsPerPage && (
                <Box className={classes.paginationSection}>
                  {sortedUsersList.length > 0 && (
                    <Box className={classes.footer}>
                      <IconButton
                        className={classes.footerPart1}
                        onMouseEnter={() => setHoveredArrowLeft(true)}
                        onMouseLeave={() => setHoveredArrowLeft(null)}
                        onClick={() => handleChangePage(null, page - 1)}
                        disabled={page === 0}
                      >
                        <ArrowLeft
                          color={
                            page === 0
                              ? '#2C313A33'
                              : hoveredArrowLeft
                              ? '#5E59F3'
                              : '#2C313A'
                          }
                        />
                      </IconButton>
                      <Box className={classes.footerPart2}>
                        <Box className={classes.pagination}>
                          Page {page + 1} of{' '}
                          {Math.ceil(sortedUsersList.length / rowsPerPage)}
                        </Box>
                      </Box>
                      <IconButton
                        className={classes.footerPart3}
                        onMouseEnter={() => setHoveredArrowRight(true)}
                        onMouseLeave={() => setHoveredArrowRight(null)}
                        onClick={() => handleChangePage(null, page + 1)}
                        disabled={
                          page >=
                          Math.ceil(sortedUsersList.length / rowsPerPage) - 1
                        }
                      >
                        <ArrowRight
                          color={
                            page >=
                            Math.ceil(sortedUsersList.length / rowsPerPage) - 1
                              ? '#2C313A33'
                              : hoveredArrowRight
                              ? '#5E59F3'
                              : '#2C313A'
                          }
                        />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              )}
            </>
          ) : (
            <Box className={classes.footerNoRec}>
              <Box className={classes.pagination}>Nothing found</Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default UsersDashboard;
